import React from "react";
import { Alert, Card, CardBody, Col, Input, Row, Table } from "reactstrap";
import { FACTURA_CAMPOS } from "../constants";
import { construirIdFacturaInput } from "../helpers";
import { FacturaSegmento } from "../types";

interface FacturasRangoStepProps {
  facturaDesde: FacturaSegmento;
  facturaHasta: FacturaSegmento;
  puedeEditar: boolean;
  tieneRangoValido: boolean;
  onDigitChange: (
    rango: "desde" | "hasta",
    campo: keyof FacturaSegmento,
    indice: number,
    valor: string,
    max: number,
    indiceGlobal?: number,
    desplazamiento?: -1 | 1,
  ) => void;
}

const FacturasRangoStep = ({ facturaDesde, facturaHasta, puedeEditar, tieneRangoValido, onDigitChange }: FacturasRangoStepProps) => (
  <Row className="g-4">
    <Col>
      <Card>
        <CardBody>
          {!puedeEditar ? (
            <Alert color="warning">Seleccioná una caja antes de completar el rango de facturas.</Alert>
          ) : (
            <div className="table-responsive">
              <Table className="align-middle">
                <thead>
                  <tr>
                    <th style={{ width: "15%" }}>Rango</th>
                    {FACTURA_CAMPOS.map((campo) => (
                      <th key={campo.campo}>{campo.etiqueta}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(["desde", "hasta"] as const).map((rango) => {
                    const valores = rango === "desde" ? facturaDesde : facturaHasta;
                    const etiqueta = rango === "desde" ? "Desde" : "Hasta";
                    return (
                      <tr key={rango}>
                        <td className="fw-semibold">{etiqueta}</td>
                        {(() => {
                          let offset = 0;
                          return FACTURA_CAMPOS.map((campo) => {
                            const longitud = campo.longitud;
                            const digitos = Array.from({ length: longitud }, (_, idx) => valores[campo.campo]?.[idx] ?? "");
                            const inicioGlobal = offset;
                            offset += longitud;
                            return (
                              <td key={`${rango}-${campo.campo}`}>
                                <div className="d-flex gap-1 flex-wrap">
                                  {digitos.map((digito, idx) => {
                                    const indiceGlobal = inicioGlobal + idx;
                                    return (
                                      <Input
                                        key={`${rango}-${campo.campo}-${idx}`}
                                        id={construirIdFacturaInput(rango, indiceGlobal)}
                                        type="text"
                                        value={digito}
                                        maxLength={1}
                                        inputMode="numeric"
                                        onChange={(event) => {
                                          const valorSanitizado = event.target.value.replace(/\D/g, "");
                                          const valorFinal = valorSanitizado.slice(-1);
                                          onDigitChange(
                                            rango,
                                            campo.campo,
                                            idx,
                                            valorFinal,
                                            longitud,
                                            indiceGlobal,
                                            valorFinal ? 1 : undefined,
                                          );
                                        }}
                                        onKeyDown={(event) => {
                                          if (event.key === "Backspace" && !digito) {
                                            onDigitChange(rango, campo.campo, idx, "", longitud, indiceGlobal, -1);
                                            event.preventDefault();
                                          }
                                        }}
                                        style={{
                                          width: 34,
                                          minWidth: 34,
                                          textAlign: "center",
                                          padding: "0.2rem 0",
                                        }}
                                      />
                                    );
                                  })}
                                </div>
                              </td>
                            );
                          });
                        })()}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              <small className="text-muted">Ejemplo: Desde 001-001-0000001 hasta 001-001-0000999</small>
              {!tieneRangoValido && (
                <Alert color="warning" className="mt-3 mb-0 py-2">
                  Completá los 13 dígitos en ambos campos para continuar.
                </Alert>
              )}
            </div>
          )}
        </CardBody>
      </Card>
    </Col>
  </Row>
);

export default FacturasRangoStep;
